import React from "react";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import { postEmployeeByEmployerId } from "../../../../slices/admin/thunk";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import { useCurrentUser } from "../../../../slices/auth/hooks";
import AddEmployeeTabs from "../../../Tabs/AddEmployeeTabs";

const AddEmployeeModal = ({
  open,
  setOpen,
  onCreate,
  activeTab,
  toggleTab,
}) => {
  const { id } = useParams();
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();

  const toggleModal = () => {
    setOpen(!open);
  };

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    tier: "employee" // Default tier is employee
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    tier: Yup.string().required("Tier is required")
  });

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setSubmitting(true);

      // Convert tier selection to isFamily boolean
      const isFamily = values.tier === "family";
      const dataToSubmit = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        isFamily
      };

      const response = await dispatch(
        postEmployeeByEmployerId({
          id: id || currentUser.id,
          data: dataToSubmit,
        })
      ).unwrap();

      if (response.error) {
        Swal.fire({
          title: "Oops!",
          text: `There was a problem adding the employee: ${response.error}.`,
          icon: "error",
          confirmButtonText: "Ok",
        });
      } else {
        // Success
        Swal.fire({
          title: "Success!",
          text: `Employee ${values.email} added successfully!`,
          icon: "success",
          confirmButtonText: "Ok",
        });

        if (onCreate) {
          onCreate();
        }

        resetForm();
        setOpen(false);
      }
    } catch (error) {
      console.error("Error adding employee:", error);
      Swal.fire({
        title: "Error",
        text: "An unexpected error occurred while adding the employee.",
        icon: "error",
        confirmButtonText: "Ok",
      });
    }
    setSubmitting(false);
  };

  return (
    <Modal isOpen={open} toggle={toggleModal} fade={false}>
      <ModalHeader toggle={toggleModal}>Add Employee</ModalHeader>
      <ModalBody>
        <div className="mb-4">
          <AddEmployeeTabs activeTab={activeTab} toggleTab={toggleTab} />
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting, values, handleChange, setFieldValue }) => {
            const isFieldsCompleted =
              values.firstName && values.lastName && values.email;

            return (
              <Form>
                <div className="mb-3">
                  <label htmlFor="firstName" className="form-label">
                    First Name
                  </label>
                  <Field
                    type="text"
                    name="firstName"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="firstName"
                    component="div"
                    className="text-danger"
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="lastName" className="form-label">
                    Last Name
                  </label>
                  <Field
                    type="text"
                    name="lastName"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="lastName"
                    component="div"
                    className="text-danger"
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    Email
                  </label>
                  <Field
                    type="email"
                    name="email"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="text-danger"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="tier" className="form-label">
                    Tier
                  </label>
                  <Field
                    as="select"
                    name="tier"
                    className="form-select"
                  >
                    <option value="employee">Employee (E)</option>
                    <option value="family">Family (F)</option>
                  </Field>
                  <ErrorMessage
                    name="tier"
                    component="div"
                    className="text-danger"
                  />
                </div>

                <Button
                  className="w-100"
                  type="submit"
                  color="primary"
                  disabled={isSubmitting || !isFieldsCompleted}
                >
                  {isSubmitting ? (
                    <div className="spinner-border spinner-border-sm me-2"></div>
                  ) : null}
                  Add Employee
                </Button>
              </Form>
            );
          }}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default AddEmployeeModal;